import { useContext, useEffect, useMemo } from 'react'
import { useQuery } from 'react-query'

import type { ArtifactChartPagePanel } from 'features/ask-yogi/types'
import { DEFAULT_PANEL_FIELDS } from 'features/charts-v2/constants'
import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'
import { isYogiQLStoredPanel } from 'features/charts-v2/types/stored-panel'
import type { ChartRequest } from 'features/charts-v2/types/yogiql'
import {
  resolveYogiQLNames,
  storedQueryFields,
  warnYogiQLFallback,
  type YogiQLNames,
} from 'features/charts-v2/utils'
import type { FilterOption } from 'features/filters/types'
import {
  projectFilterListKey,
  ProjectFilterListOwnerContext,
} from 'src/hooks/use-project-filter-list'

/** What the chat draws for one chart artifact. */
export type YogiQLChatChart =
  | { status: 'legacy' }
  | { status: 'pending' }
  | { status: 'unavailable' }
  | { status: 'ready'; panel: ChartPanel; names: YogiQLNames }

/** A YogiQL chat chart's artifact request is `{request: <ChartRequest>, chart_type}`. */
export const isYogiQLChatRequest = (
  request: Record<string, unknown>,
): boolean =>
  typeof request['request'] === 'object' && request['request'] !== null

/** A stored request as a panel, or null after recording why it does not read. */
const readRequest = (
  stored: ChartRequest,
  options: FilterOption[] | null,
  reasons: string[],
): ChartPanel | null => {
  if (!options) return null
  let fields: ReturnType<typeof storedQueryFields>
  try {
    fields = storedQueryFields(stored, { filterOptions: options })
  } catch (error) {
    fields = `the request does not read: ${String(error)}` // not a crash
  }
  if (typeof fields === 'string') {
    reasons.push(fields)
    return null
  }
  return { ...DEFAULT_PANEL_FIELDS, ...fields } as ChartPanel
}

/**
 * Decode a YogiQL chat chart, and the page panels of a page-write lineage
 * chart, with the hub's filter options. No `enhanced_dialect` dependency: the
 * chat reads YogiQL on every hub. The options are read from the project
 * root's query, never fetched here; options that will not arrive (failed, or
 * no root runs the query) leave the chart `unavailable`, never a legacy query.
 * A page panel that does not decode is null (left out of "View in charts",
 * never replaced by the default chart), keeping the others' positions. Each
 * reason is warned once.
 */
export function useYogiQLChatChart(
  projectId: string | null,
  request: Record<string, unknown>,
  pagePanels: ArtifactChartPagePanel[] | undefined,
): {
  chart: YogiQLChatChart
  pagePanels: Array<ArtifactChartPagePanel | null> | undefined
} {
  const { data: filterOptions, status } = useQuery<FilterOption[]>(
    projectFilterListKey(projectId ?? ''),
    { enabled: false },
  )
  const rootLoadsOptions =
    useContext(ProjectFilterListOwnerContext) === projectId
  const optionsPending =
    status === 'loading' || (status === 'idle' && rootLoadsOptions)
  const options = filterOptions?.length ? filterOptions : null

  const decoded = useMemo(() => {
    const reasons: string[] = []
    let chart: YogiQLChatChart = { status: 'legacy' }
    if (isYogiQLChatRequest(request)) {
      if (optionsPending) chart = { status: 'pending' }
      else if (!options) {
        reasons.push('the hub filter options did not load')
        chart = { status: 'unavailable' }
      } else {
        const panel = readRequest(
          request['request'] as ChartRequest,
          options,
          reasons,
        )
        chart = panel
          ? {
              status: 'ready',
              panel,
              names: resolveYogiQLNames(panel, options),
            }
          : { status: 'unavailable' }
      }
    }

    const panels = pagePanels?.map((p) => {
      if (!isYogiQLStoredPanel(p.panel)) return p
      const panel = readRequest(p.panel.request, options, reasons)
      return panel ? { ...p, panel } : null
    })
    return { chart, pagePanels: panels, reasons }
  }, [request, pagePanels, options, optionsPending])

  const warning = decoded.reasons.join('; ')
  useEffect(() => {
    if (warning) warnYogiQLFallback(`Ask Yogi chat chart: ${warning}`)
  }, [warning])

  return { chart: decoded.chart, pagePanels: decoded.pagePanels }
}
